/* eslint-disable no-param-reassign */
import { CookieSerializeOptions, parse, serialize } from 'cookie'
import { NextPageContext } from 'next'
import React, { KeyboardEvent } from 'react'

import { AVATAR_COLORS, LABEL_COLORS, Environments, UNWANTED, TESTING } from '../constants'

import { LaunchModuleApprovalStatus } from '~generated/types'
import { AddEventListener } from '~types/utils'

const freeEmailDomains = require('free-email-domains')

export const isPlainObject = (candidate: unknown) => {
  return typeof candidate === 'object' && candidate !== null && !Array.isArray(candidate)
}

export const isArray = (candidate: unknown) => {
  return Array.isArray(candidate)
}

export const isString = (candidate: unknown) => {
  return typeof candidate === 'string'
}

export const isNumber = (candidate: unknown) => {
  return typeof candidate === 'number' && Number.isFinite(candidate)
}

export const isBoolean = (candidate: unknown) => {
  return typeof candidate === 'boolean'
}

export const isFunction = (candidate: unknown) => {
  return (
    candidate &&
    (Object.prototype.toString.call(candidate) === '[object Function]' ||
      typeof candidate === 'function' ||
      candidate instanceof Function)
  )
}

export const isBrowser = () => {
  return typeof window !== 'undefined'
}

export const isEmpty = (value?: string | Record<string, any> | unknown[]) => {
  return !value || (value && Object.values(value).length === 0)
}

export const isElement = (element: unknown) => {
  return element instanceof HTMLElement || element instanceof SVGElement
}

/**
 * Hex decimal color validator
 */

const HEX_COLOR_REGEXP = /^#[\da-f]{6}$/i

export const validateHexColor = (color: string) => {
  return HEX_COLOR_REGEXP.test(color)
}

/**
 * Email validator
 */

export const EMAIL_REGEXP =
  /[\d!#$%&'*+/=?^_`a-z{|}~-]+(?:\.[\d!#$%&'*+/=?^_`a-z{|}~-]+)*@(?:[\da-z](?:[\da-z-]*[\da-z])?\.)+[\da-z](?:[\da-z-]*[\da-z])?/

export const isValidEmail = (email: string) => {
  return EMAIL_REGEXP.test(email)
}

/**
 * Password validator
 */

const PASSWOR_REGEXP = /^(?=.*\d)(?=.*[A-Z]).{6,}$/

export const validatePassword = (password: string) => {
  return PASSWOR_REGEXP.test(password)
}

export const plug = (): null => null

export const getIsomorphicCookie = (req?: NextPageContext['req']) => {
  return isBrowser() ? parse(document.cookie) : parse(req?.headers?.cookie ?? '')
}

export const addEventListener: AddEventListener = (element, event, callback, options) => {
  element?.addEventListener(event, callback, options)
  return () => element?.removeEventListener(event, callback)
}

export const onKeyPressHandler =
  <E extends HTMLElement = HTMLElement>(data: string | string[], callback: (ev: KeyboardEvent<E>) => void) =>
  (ev: KeyboardEvent<E>) => {
    if ((Array.isArray(data) && data.includes(ev.key)) || ev.key === data) {
      callback(ev)
    }
  }

export const omit = <T extends Record<string | number | symbol, any>, K extends (keyof T | string)[]>(
  input: T | T[] = [],
  keys: K
) => {
  if (Array.isArray(input)) {
    return input.map((obj) => omit(obj, keys))
  }

  return keys.reduce(
    (accum, key) => {
      delete accum[key]
      return accum
    },
    { ...input }
  )
}

export const omitDeep = <T extends Record<string, any>, K extends (keyof T)[]>(input: T, keys: K) => {
  return Object.keys(input).reduce(
    (acc, key) => {
      if (keys.includes(key)) {
        delete acc[key]
      }

      if (isPlainObject(acc[key])) {
        acc[key as keyof T] = omitDeep(acc[key], keys)
      }

      return acc
    },
    { ...input }
  )
}

/**
 * Helper function to get unique object like structures from array by key
 */
export function uniqueBy<T = Record<string, any>>(key: string, arr: T[] = []) {
  const itemsMap = arr.reduce<Record<string, T>>((acc, item) => {
    if (item[key]) acc[item[key]] = item
    return acc
  }, {})

  return Object.values(itemsMap)
}

// module statuses

export const isNotRequested = (value: LaunchModuleApprovalStatus) => {
  return value === LaunchModuleApprovalStatus.NOT_REQUESTED
}

export const isReadyToRequest = (value: LaunchModuleApprovalStatus) => {
  return value === LaunchModuleApprovalStatus.READY_TO_REQUEST
}

export const isApproving = (value: LaunchModuleApprovalStatus) => {
  return value === LaunchModuleApprovalStatus.APPROVING
}

export const isApproved = (value: LaunchModuleApprovalStatus) => {
  return value === LaunchModuleApprovalStatus.APPROVED
}

export const isMobile = (userAgent: string) =>
  Boolean(/android|blackberry|iphone|ipad|ipod|opera mini|iemobile|wpdesktop/i.test(userAgent))

export const isLocal = (): boolean => Environments.LOCAL === process.env.NEXT_PUBLIC_ENVIRONMENT

export const isProduction = (): boolean => Environments.PRODUCTION === process.env.NEXT_PUBLIC_ENVIRONMENT

export const setCookies = (fields: Record<string, string>, maxAge?: number) => {
  Object.entries(fields).forEach(([key, value]) => {
    document.cookie = serialize(key, value, {
      sameSite: 'lax',
      httpOnly: false,
      path: '/',
      maxAge: maxAge !== undefined ? maxAge : 30 * 60 * 1000 * 10,
    })
  })
}

/**
 * Create list of cookies for "Set-Cookie" header
 */
export const setServerSideCookie = (cookie: Record<string, string>, options?: CookieSerializeOptions) => {
  return Object.entries(cookie).map(([key, value]) => serialize(key, value, options))
}

export const clearCookies = (keys: string[]) => {
  keys.forEach((key) => {
    document.cookie = serialize(key, '', {
      path: '/',
      maxAge: -1,
    })
  })
}

export const isNewDesign = (variant) =>
  variant?.startsWith('new') || variant === 'orange' || variant === 'lavender' || variant === 'black'

const dollarUseLocale = Intl.NumberFormat('en-US')
export const formatCurrency = (a: number): string | null => (a ? dollarUseLocale.format(a) : null)

export const clearString = (value?: string) => {
  return typeof value === 'string' ? value.trim().toLocaleLowerCase() : ''
}

export const calculateAvatarColor = (value = '') => {
  let i = 0
  let sum = 0

  while (i < value?.length) {
    sum += value.charCodeAt(i)
    i += 1
  }

  return AVATAR_COLORS[sum % AVATAR_COLORS.length]
}

export const calculateLabelColor = () => {
  const randNum = Math.floor(Math.random() * 6)

  return LABEL_COLORS[randNum]
}

export const formatId = (...parts: (string | number)[]) => parts.filter(Boolean).join('-')

export function preventDefault(e: React.SyntheticEvent) {
  e.preventDefault()
}

export function stopPropagation(e: React.SyntheticEvent) {
  e.stopPropagation()
}

export function mergeRefs(...refs: React.MutableRefObject<HTMLElement>[]) {
  return (node: HTMLElement) => {
    refs.forEach((r) => {
      const ref = r

      if (ref) {
        ref.current = node
      }
    })
  }
}

export function percentage(partial: number, total: number) {
  return Math.round((100 * partial) / total) || 0
}

/**
 * Sort object to generate key from variables in apollo cache for paginated queries
 */

export const sortObject = (unordered, sortArrays = true) => {
  if (!unordered || typeof unordered !== 'object') {
    return unordered
  }

  if (Array.isArray(unordered)) {
    const newArr = unordered.map((item) => sortObject(item, sortArrays))
    if (sortArrays) {
      newArr.sort()
    }
    return newArr
  }

  const ordered = {}

  Object.keys(unordered)
    .sort()
    .forEach((key) => {
      ordered[key] = sortObject(unordered[key], sortArrays)
    })

  return ordered
}

/**
 * Merge two objects deeply. All non primitive values except object will be overridden.
 */

export const mergeObjects = <T1 extends ObjectType = ObjectType, T2 extends ObjectType = ObjectType>(
  obj1: T1,
  obj2: T2
) => {
  return (Object.keys(obj1) as (keyof T1 & keyof T2)[]).reduce(
    (accum, key) => {
      if (isPlainObject(obj1[key]) && isPlainObject(obj2[key])) {
        accum[key] = mergeObjects(obj1[key], obj2[key])
      } else {
        accum[key] = (key as string) in obj2 ? obj2[key] : obj1[key]
      }
      return accum
    },
    { ...obj1, ...obj2 }
  )
}

export function isStringNumber(value: string) {
  return Number.isNaN(Number(value))
}

export function isStringNumbersWithDashBetween(value: string) {
  const dashCount = value?.match(/-/g)?.length
  const isDashExist = dashCount > 0

  if (dashCount > 1) return false

  if (isDashExist) {
    const [leftNumber, rightNumber] = value.split('-')

    if (isStringNumber(leftNumber)) return false
    if (rightNumber && isStringNumber(rightNumber)) return false
  } else {
    if (value?.startsWith('-')) return false
    if (isStringNumber(value)) return false
  }

  return true
}

/**
 * Check if email is in testing emails or domains.
 * @param email
 */
export function isTestingEmail(email: string) {
  const validators = []

  let hasTestingEnding = false
  TESTING.DOMAINS_ENDINGS.forEach((ending) => {
    if (email.includes(ending)) {
      hasTestingEnding = true
    }
  })

  validators.push(TESTING.EMAILS.includes(email), hasTestingEnding)

  return validators.some(Boolean)
}

/**
 * Check if email is in unwanted emails or domains.
 * @param email
 */
export function isUnwanted(email: string) {
  let emailDomain
  let emailDomainEnding
  if (email.includes('@')) {
    // eslint-disable-next-line prefer-destructuring
    emailDomain = email.split('@')[1]
    const domainParts = emailDomain.split('.')
    emailDomainEnding = domainParts[domainParts.length - 1]
  } else {
    emailDomain = email
  }

  const validators = []

  validators.push(
    freeEmailDomains.includes(emailDomain),
    UNWANTED.DOMAINS.includes(emailDomain),
    UNWANTED.DOMAINS_ENDINGS.includes(emailDomainEnding)
  )

  return validators.some(Boolean)
}

export function formatIndex(idx: number) {
  const minLen = 2
  let num = String(idx + 1)

  while (num.length !== minLen) {
    num = `0${num}`
  }

  return num
}

export function substractArrays<T>(left: T[], right: T[]) {
  return left.filter((n) => !right.includes(n))
}

// eslint-disable-next-line consistent-return
export function locationOf<T extends any>(
  item: T,
  arr: T[],
  compareFn: (a: T, b: T) => number,
  start?: number,
  end?: number
) {
  if (arr.length === 0) return -1
  start = start || 0
  // eslint-disable-next-line unicorn/explicit-length-check
  end = end || arr.length

  // eslint-disable-next-line no-bitwise
  const pivot = (start + end) >> 1
  // console.log({
  //   compareFn,
  //   item,
  //   start,
  //   pivot,
  //   arr,
  // })
  const c = compareFn(item, arr[pivot])

  if (end - start <= 1) return c === -1 ? pivot - 1 : pivot

  switch (c) {
    case -1:
      return locationOf(item, arr, compareFn, start, pivot)
    case 0:
      return pivot
    case 1:
      return locationOf(item, arr, compareFn, pivot, end)
    default:
      console.error(`[locationOf] Unexpected result of compareFn call: ${c}`)
  }
}

export const convertArrayToObjectByKey = (array, key) => {
  const initialValue = {}
  return array.reduce((obj, item) => {
    return {
      ...obj,
      [item[key]]: item,
    }
  }, initialValue)
}

export const removeEmptyStringsFromObject = <T extends Record<string, unknown>>(object: T) => {
  const newObject = { ...object }

  Object.entries(newObject).forEach(([key, value]) => {
    if (value === '') {
      delete newObject[key]
    }
  })

  return newObject
}

// Recursively check for populated or nonnull content. If none found, return `true`. Recursive so [{}] will be treated as empty.
function isNothing(item: unknown) {
  // null / undefined
  if (item == null) return true

  // deep object falsiness
  if (typeof item === 'object') {
    if (Array.isArray(item)) {
      // array -> check for populated/nonnull value

      const found = item.some((el) => !isNothing(el))
      return !found
    }
    // other object -> check for populated/nonnull value
    const found = [...Object.values(item)].some((v) => !isNothing(v))
    return !found
  }

  return false
}

export function removeEmptyFields(obj: Record<string, unknown> | Record<string, unknown>[]) {
  // clean array
  if (Array.isArray(obj)) {
    return obj
      .map((el) => {
        if (isNothing(el)) return null

        if (typeof el === 'object') {
          // recurse if it's a truthy object
          return removeEmptyFields(el)
        }

        return el
      })
      .filter(Boolean) // remove value if falsy
  }

  if (isPlainObject(obj)) {
    return [...Object.entries(obj)].reduce((acc, [k, v]) => {
      if (isNothing(v)) return acc

      if (typeof v === 'object') {
        const newValue = removeEmptyFields(v as Record<string, string>)
        if (newValue != null) acc[k] = newValue
      } else {
        acc[k] = v
      }

      return acc
    }, {})
  }

  return obj
}

export function markdownToHtml(markdown: string) {
  // Replace line breaks with <br> tags
  markdown = markdown.replace(/\n/g, '<br>')
  // Replace bold and italic together
  markdown = markdown.replace(/\*{3}(.+?)\*{3}/g, '<strong><em>$1</em></strong>')

  // The other patterns are for cases when there are spaces between the asterisks
  markdown = markdown.replace(/\*\*\s\*(.+?)\*\*\s\*/g, '<strong><em>$1</em></strong>')
  markdown = markdown.replace(/\*\*\s\*\s(.+?)\*\*\s\*/g, '<strong><em>$1</em></strong>')
  markdown = markdown.replace(/\*{3}\s(.+?)\*{3}/g, '<strong><em>$1</em></strong>')

  // Replace bold
  markdown = markdown.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')

  // Replace italic
  markdown = markdown.replace(/\*(.+?)\*/g, '<em>$1</em>')
  return markdown
}

export function removeLineBreaks(text: string) {
  text = text.replace(/\s*\n\s*/g, '')
  text = text.replace(/\s*\n\s*/g, '')
  return text
}

export function isMac() {
  return isBrowser() && navigator.platform.toUpperCase().includes('MAC')
}

export function numberWithCommas(x: number) {
  return x.toString().replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ',')
}
